import React, { useState, useEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import { useMediaQuery, useTheme, Box } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { CircleRounded } from '@mui/icons-material';
import { useAppSettings } from '../../../../../../../app/features/settings/providers/AppSettingsProvider';
import useAppFormatAmount from '../../../../../../../app/features/format/hooks/useAppFormatAmount';
import useAppFormatNumber from '../../../../../../../app/features/format/hooks/useAppFormatNumber';
import styleVariables from '../../../../../../../app/styles/styleVariables';
import am5locales_fr_FR from '@amcharts/amcharts5/locales/fr_FR';
import am5locales_en_US from '@amcharts/amcharts5/locales/en_US';
import investmentKpiLang from '../../../../../langs/investmentKpiLang';
import AppFormatAmount from '../../../../../../../app/features/format/AppFormatAmount';
import { TInvestFundCurrency } from '../../../../../types/fund/TInvestFundCurrency';
import CGraphMultiCurrency from '../../../../../components/CGraphMultiCurrency';

interface IChartData {
	value: number;
	sliceColor: string;
}

type CSynthesisGraphTotalValueProps = {
	color?: string;
	position: number;
	distributionsRecallable: number;
	netAssetValue: number;
	totalDistributed: number;
	currency: TInvestFundCurrency;
};

const CSynthesisGraphTotalValue = (props: CSynthesisGraphTotalValueProps) => {
	const { color, position, distributionsRecallable, netAssetValue, totalDistributed, currency } = props;

	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { rounding } = useAppSettings();
	const { displayRounding, displayRoundingSuffix, displayCurrencySymbol } = useAppFormatAmount();
	const { formatPercent } = useAppFormatNumber();

	const darkModeEnabled = theme.palette.mode === 'dark';

	const [percentCommittedCapital] = useState(0);
	const [chartData, setChartData] = useState<IChartData[]>([]);

	const listColors = styleVariables.sliceColors.lightMode.map((x) => am5.color(x));

	useEffect(() => {
		const data = [] as IChartData[];

		// totalDistributed
		data.push({
			value: displayRounding(totalDistributed, rounding),
			sliceColor: listColors[7].toString(),
		});

		// netAssetValue
		data.push({
			value: displayRounding(netAssetValue, rounding),
			sliceColor: color === 'primary' || darkModeEnabled ? '#FFF' : listColors[4].toString(),
		});

		setChartData(data);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		totalDistributed,
		netAssetValue,
		t,
		darkModeEnabled,
		formatPercent,
		displayRounding,
		rounding,
		displayRoundingSuffix,
		theme.palette,
		color,
	]);

	useEffect(() => {
		if (position !== undefined && chartData.length > 0 && currency) {
			const root = am5.Root.new('synthesis-graph-total_value-' + position.toString());
			if (darkModeEnabled) {
				root.setThemes([am5themes_Dark.new(root)]);
			} else {
				root.setThemes([am5themes_Animated.new(root)]);
			}

			if (i18n.language === 'fr') {
				root.locale = am5locales_fr_FR;
			} else {
				root.locale = am5locales_en_US;
			}

			const chart = root.container.children.push(
				am5percent.PieChart.new(root, {
					layout: root.verticalLayout,
					innerRadius: am5.percent(60),
				}),
			);

			const series = chart.series.push(
				am5percent.PieSeries.new(root, {
					valueField: 'value',
					alignLabels: true,
					innerRadius: am5.percent(50),
				}),
			);

			series?.get('colors')?.set(
				'colors',
				chartData.map((x: IChartData) => am5.color(x.sliceColor)),
			);

			series.ticks.template.setAll({ forceHidden: true });
			series.labels.template.setAll({ forceHidden: true });
			series.slices.template.states.create('hover', { scale: 1 });
			series.slices.template.setAll({
				tooltipText: undefined,
				forceInactive: true,
				scale: 1.1,
			});

			series.data.setAll(chartData);

			series.appear(1000, 100);

			chart.seriesContainer.children.push(
				am5.Label.new(root, {
					textAlign: 'center',
					y: 32,
					centerY: am5.percent(100),
					fill: am5.Color.fromString(
						color === 'primary' ? theme.palette.primary.contrastText : theme.palette.primary.main,
					),
					centerX: am5.p50,
					text: `[bold fontSize:30px]${displayRounding(
						netAssetValue + totalDistributed,
						rounding,
					)} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(
						currency,
					)}[/]\n[bold fontSize:1.125rem]${t(investmentKpiLang.total_value.tag)}[/]`,
				}),
			);

			return () => {
				root.dispose();
			};
		}
	}, [
		chartData,
		darkModeEnabled,
		percentCommittedCapital,
		t,
		isMobile,
		rounding,
		i18n.language,
		displayRoundingSuffix,
		displayCurrencySymbol,
		position,
		theme.palette.primary.main,
		displayRounding,
		netAssetValue,
		color,
		theme.palette.primary.contrastText,
		totalDistributed,
		currency,
	]);

	return (
		<>
			{position !== undefined && chartData && chartData.length > 0 ? (
				<>
					{currency ? (
						<>
							<Box
								id={`synthesis-graph-total_value-${position.toString()}`}
								style={{ height: '380px', margin: '0px', padding: '0px' }}
							/>
						</>
					) : (
						<CGraphMultiCurrency height={380} />
					)}
					<Box sx={{ fontSize: '1.125rem', textAlign: 'left' }}>
						<CircleRounded style={{ color: chartData[0].sliceColor, fontSize: '0.85rem', margin: '0px' }} />{' '}
						{t(investmentKpiLang.distributions.tag)}{' '}
						<Box component="span" sx={{ fontWeight: 600 }}>
							<AppFormatAmount value={totalDistributed} currency={currency} />
						</Box>{' '}
						<CircleRounded style={{ color: chartData[1].sliceColor, fontSize: '0.85rem', margin: '0px' }} />{' '}
						{t(investmentKpiLang.nav.tag)}{' '}
						<Box component="span" sx={{ fontWeight: 600 }}>
							<AppFormatAmount value={netAssetValue} currency={currency} />
						</Box>
					</Box>

					{distributionsRecallable && distributionsRecallable > 0 ? (
						<Box sx={{ fontSize: '0.85rem', textAlign: 'left' }}>
							{t(investmentKpiLang.including_recallable.tag)} :{' '}
							<Box component="span" sx={{ fontWeight: 600 }}>
								<AppFormatAmount value={distributionsRecallable} currency={currency} />
							</Box>
						</Box>
					) : (
						''
					)}
				</>
			) : (
				''
			)}
		</>
	);
};

export default CSynthesisGraphTotalValue;
